import React from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useBrainBankSync } from "../../context/BrainBankSyncContext";

/**
 * One line under a sent attachment: where its BrainBank copy is. Nothing for
 * attachments that were never sent to a BrainBank.
 */
const AttachmentSyncStatus: React.FC<{ sessionId: string; fileName: string }> = ({
  sessionId,
  fileName,
}) => {
  const { getAttachmentSync, retryAttachmentSync } = useBrainBankSync();
  const sync = sessionId ? getAttachmentSync(sessionId, fileName) : undefined;
  if (!sync) return null;

  switch (sync.status) {
    case "checking":
      return null;
    case "sending":
    case "not_processed":
    case "processing":
      return (
        <div className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-400">
          <Loader2 size={11} className="animate-spin" />
          Processing in BrainBank
        </div>
      );
    case "processed":
      return (
        <div className="flex items-center gap-1 text-[11px] text-green-700 dark:text-green-400">
          <CheckCircle2 size={11} />
          In BrainBank
        </div>
      );
    case "failed":
      return (
        <div className="text-[11px] text-red-700 dark:text-red-400">
          BrainBank couldn't process this file
        </div>
      );
    case "stalled":
      return (
        <div className="text-[11px] text-amber-700 dark:text-amber-400">
          Still processing in BrainBank
        </div>
      );
    case "not_uploader":
      return (
        <div className="text-[11px] text-subtle">
          Not added: only the person who uploaded it can add it
        </div>
      );
    case "removed":
      return <div className="text-[11px] text-subtle">Removed from BrainBank</div>;
    case "no_permission":
      return (
        <div className="text-[11px] text-subtle">Not added: no permission for this BrainBank</div>
      );
    case "send_failed":
      return (
        <div className="flex items-center gap-1.5 text-[11px] text-red-700 dark:text-red-400">
          Couldn't add to BrainBank
          <button
            type="button"
            onClick={() => retryAttachmentSync(sessionId, fileName)}
            className="font-semibold text-violet-700 dark:text-violet-300 hover:underline"
          >
            Retry
          </button>
        </div>
      );
    default:
      return null;
  }
};

export default AttachmentSyncStatus;
